import React, { useState, useEffect, useRef } from 'react';
import { Bot, ChevronLeft, Send, User, Plus, ChevronDown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import "./ExplorePage.css";

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function ExplorePage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>(() => {
    const saved = localStorage.getItem('explorer_chat');
    return saved ? JSON.parse(saved) : [
      { role: 'assistant', content: 'Hi - I can help you explore where agents could create value in your Microsoft environment. Where would you like to start?' }
    ];
  });
  const [input, setInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [loadingData, setLoadingData] = useState<any>(null);
  const [bgAnimationData, setBgAnimationData] = useState<any>(null);

  useEffect(() => {
    fetch('/Group-1.json')
      .then(res => res.json())
      .then(data => setBgAnimationData(data));
  }, []);

  useEffect(() => {
    localStorage.setItem('explorer_chat', JSON.stringify(messages));
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const file = Math.random() > 0.5 ? '/loading2_white.json' : '/loading3_white.json';
      fetch(file)
        .then(res => res.json())
        .then(data => setLoadingData(data));
    }
  }, [isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const newMsg: Message = { role: 'user', content: input };
    const newMessages = [...messages, newMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(process.env.REACT_APP_API_URL || '/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: newMsg.content,
          history: messages.filter(m => m.content !== 'Hi - I can help you explore where agents could create value in your Microsoft environment. Where would you like to start?').map(m => ({ role: m.role, content: m.content }))
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch response');
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer || 'No answer received.' }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const suggestions = [
    "What kind of technology challenges can Findmore help with?",
    "Can Findmore help us improve or automate our business processes?",
    "Do you have experience with Microsoft Dynamics 365 or Power Platform?",
    "Can you suggest consultant profiles for a project we have in mind?",
    "What types of projects has Findmore worked on before?",
    "How can we get in touch with the Findmore team?"
  ];

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    setIsDropdownOpen(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  return (
    <div className="explore-page-wrapper">
      <div className="lottie-background-2">
        {bgAnimationData && (
          <Lottie
            lottieRef={lottieRef}
            animationData={bgAnimationData}
            loop={true}
            onDOMLoaded={() => lottieRef.current?.setSpeed(0.3)}
          />
        )}
      </div>
      <div className="header-actions">
        <div>
          <a style={{ margin: 0, padding: 0 }} onClick={() => navigate('/')} className="back-home">
            <ChevronLeft size={18} />Back
          </a>
        </div>
      </div>
      <div className="explore-page-body">
        <div className="chat-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0 20px 0', width: '100%' }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '1rem', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
              <div style={{ background: msg.role === 'user' ? 'var(--accent-blue)' : 'rgba(255,255,255,0.1)', padding: '0.5rem', borderRadius: '50%', height: '40px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {msg.role === 'user' ? <User size={20} /> : <Bot size={20} />}
              </div>
              <div style={{ background: msg.role === 'user' ? 'var(--accent-blue)' : 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '12px', maxWidth: '80%', lineHeight: '1.5', whiteSpace: 'pre-wrap', border: msg.role === 'assistant' ? '1px solid var(--card-border)' : 'none' }}>
                {msg.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ background: 'rgba(255,255,255,0.1)', padding: '0.2rem', borderRadius: '50%', height: '40px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                {loadingData && (
                  <Lottie animationData={loadingData} loop={true} style={{ width: '50px', height: '50px' }} />
                )}
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem', borderRadius: '12px', maxWidth: '80%', fontStyle: 'italic', opacity: 0.7, display: 'flex', alignItems: 'center' }}>
                Thinking...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      <div className="chat-input-wrapper">
        <div className="chat-input-container">
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
          />
          <div className="chat-input-bottom">
            <div className="chat-input-actions">
              <div className="action-item-container">
                <div className="action-item" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  Don't know what to ask? <ChevronDown size={14} />
                </div>
                {isDropdownOpen && (
                  <div className="prompt-dropdown">
                    {suggestions.map((s, i) => (
                      <div key={i} className="dropdown-item" onClick={() => handleSuggestionClick(s)}>
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              className="send-btn"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div >
  );
}